import { colors } from '@fd/design-tokens';
import type { FlowGraph, FlowNode } from '@fd/finance-core';
import { money } from '@fd/money';
import type { Account, Category, CategoryGroup, Person } from '@fd/schemas';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../app/i18n.tsx';
import type { FlowBudget } from '../app/budget-plan.ts';
import { groupShade } from '../app/category-colors.ts';
import { categoryRanks, orderCategories } from '../app/category-order.ts';
import { useTheme } from '../app/theme.tsx';
import { groupLabel } from './CategoryOptions.tsx';
import { FlowDrill, type DrillNode } from './FlowDrill.tsx';
import { categoryLabel } from './TransactionRow.tsx';

// Where the money came from and went, taken apart in the manner of Open Books (the owner,
// 11.10.2026: "this one view, the rest is not needed"): the whole on the left, its parts as tiles
// to enter (FlowDrill). Here the graph's nodes get their names, colours and the owner's order.

/** Below these the three columns, then the bands, cannot be read (the block then stands above the tiles). */
const THREE = 880;
const TWO = 640;

export function FlowDiagram({
  graph,
  categories,
  currency,
  incomeMinor,
  groups = [],
  persons = [],
  accounts = [],
  budget = null,
  onOpen,
}: {
  graph: FlowGraph;
  categories: readonly Category[];
  groups?: readonly CategoryGroup[];
  persons?: readonly Person[];
  /** Names and colours of the savings and investment accounts the savings went to. */
  accounts?: readonly Account[];
  currency: string;
  incomeMinor: bigint;
  /** The month's budget: a limit on each tile that has one, and the total under the diagram. */
  budget?: FlowBudget | null;
  /** A category's (or a whole group's) entries, opened from its tile. */
  onOpen?(node: FlowNode): void;
}) {
  const t = useT();
  const { resolved, privacy } = useTheme();
  const palette = colors[resolved];
  const [width, setWidth] = useState(800);

  // The chart box appears later than the component (empty period first), so the size is
  // watched from the moment the element itself mounts, not the component.
  const observer = useRef<ResizeObserver | null>(null);
  const box = useCallback((element: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!element) return;
    // A hidden tab or pane reports 0: keep the last real width instead of shrinking to the minimum.
    const measure = (value: number) => value > 0 && setWidth(Math.max(300, value));
    measure(element.getBoundingClientRect().width);
    observer.current = new ResizeObserver(([entry]) => measure(entry!.contentRect.width));
    observer.current.observe(element);
  }, []);
  useEffect(() => () => observer.current?.disconnect(), []);

  const labelled = useMemo(() => {
    const byId = new Map(categories.map((c) => [c.id, c]));
    const limitOf = (key: string | null | undefined) => {
      const limit = key ? budget?.limits.get(key) : undefined;
      return limit ? { limitMinor: limit } : {};
    };
    const describe = ({ children: parts, ...node }: FlowNode): DrillNode => {
      const category = node.categoryId ? byId.get(node.categoryId) : undefined;
      switch (node.kind) {
        case 'income': {
          if (node.key === 'income:other') return { ...node, label: t.t('dashboard.flowOtherIncome'), color: palette.fill.income, icon: 'circle-dashed' };
          const person = node.personId ? persons.find((p) => p.id === node.personId) : undefined;
          const tint = category?.color ?? palette.fill.income;
          // Whose it is goes on a line of its own, so a long name does not cut it off.
          return { ...node, label: categoryLabel(t, category), sub: person?.displayName ?? null, color: tint, icon: category?.icon ?? 'wallet' };
        }
        case 'fromBalance':
          return { ...node, label: t.t('dashboard.flowFromBalance'), color: palette.fill.neutral, icon: 'landmark' };
        case 'expense': {
          const group = node.groupId ? groups.find((g) => g.id === node.groupId) : undefined;
          if (group) {
            const color = group.color ?? categories.find((c) => c.groupId === group.id)?.color ?? palette.fill.neutral;
            const children = (parts ?? []).map(({ children: _none, ...child }) => {
              const own = child.categoryId ? byId.get(child.categoryId) : undefined;
              return { ...child, label: categoryLabel(t, own), color: own?.color ?? color, icon: own?.icon ?? null, ...limitOf(child.categoryId) };
            });
            // The categories inside, largest first, under the group's name (and whole on hover).
            const inside = [...children].sort((a, b) => (b.valueMinor > a.valueMinor ? 1 : b.valueMinor < a.valueMinor ? -1 : 0)).map((c) => c.label).join(', ');
            return { ...node, label: groupLabel(group), sub: inside || null, ...(inside ? { hint: inside } : {}), color, icon: group.icon ?? null, ...limitOf(`g:${group.id}`), children };
          }
          return { ...node, label: categoryLabel(t, category), color: category?.color ?? palette.fill.neutral, icon: category?.icon ?? null, ...limitOf(node.categoryId) };
        }
        case 'otherExpense':
          return { ...node, label: t.plural('dashboard.flowOther', node.folded ?? 0), color: palette.fill.neutral, icon: 'circle-dashed' };
        case 'savings': {
          if (node.accountId) {
            const account = accounts.find((a) => a.id === node.accountId);
            return { ...node, label: account?.name ?? t.t('dashboard.flowSavings'), color: account?.color ?? (account?.type === 'INVESTMENT' ? palette.fill.investment : palette.fill.savings), icon: 'piggy-bank' };
          }
          // The accounts it went to, as a group's categories: shades of the savings colour.
          const children = (parts ?? []).map((child, index, all) => ({ ...describe(child), color: groupShade(palette.fill.savings, index, all.length) }));
          return { ...node, label: t.t('dashboard.flowSavings'), color: palette.fill.savings, icon: 'piggy-bank', ...(children.length > 0 ? { children } : {}) };
        }
        case 'elsewhere':
          return { ...node, label: t.t('dashboard.flowElsewhere'), color: palette.fill.neutral, icon: 'circle-dashed' };
        case 'surplus': {
          const account = node.accountId ? accounts.find((a) => a.id === node.accountId) : undefined;
          return { ...node, label: account ? t.t('dashboard.flowStayedOn', { account: account.name }) : t.t('dashboard.flowSurplus'), color: palette.fill.income };
        }
      }
    };
    // A group's categories in the owner's order from Budgets (D-036).
    const ranks = categoryRanks(orderCategories(categories.filter((c) => c.kind === 'EXPENSE'), groups, (x) => ('kind' in x ? categoryLabel(t, x) : groupLabel(x)), t.locale));
    const targets = graph.targets
      .map(describe)
      .map((node) => (node.children ? { ...node, children: [...node.children].sort((a, b) => (ranks.inGroup.get(a.categoryId ?? '') ?? 1e9) - (ranks.inGroup.get(b.categoryId ?? '') ?? 1e9)) } : node));
    return { sources: graph.sources.map(describe), targets };
  }, [graph, categories, groups, persons, accounts, budget, palette, t]);

  const amount = (value: bigint) => (privacy ? '•••' : t.money(money(value, currency)));
  const opens = (node: FlowNode) => onOpen !== undefined && (node.kind === 'expense' || node.kind === 'income') && node.key !== 'income:other';

  if (graph.hubMinor === 0n) {
    return <p className="text-secondary" style={{ padding: '40px 0', textAlign: 'center' }}>{t.t('dashboard.flowEmpty')}</p>;
  }

  return (
    <div ref={box} className="flow">
      <FlowDrill
        sources={labelled.sources}
        targets={labelled.targets}
        incomeMinor={incomeMinor}
        budget={budget}
        mode={width >= THREE ? 'three' : width >= TWO ? 'two' : 'stack'}
        privacy={privacy}
        amount={amount}
        opens={opens}
        {...(onOpen ? { onOpen } : {})}
      />
    </div>
  );
}
